import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SearchAsyncSelectField } from '@electricjs/arc';

import { EmployeeSelectOption } from '@/components/EmployeeSelectOption';
import { EmployeeOption } from '@/components/People/EmployeeGroups/NewEmployeeGroupForm';
import EmployeeOnLeaveIcon from '@/components/People/EmployeeOnLeaveIcon';
import {
  appendSearchHint,
  EMPLOYEE_SEARCH_HINT_VALUE,
  transformEmployeesToOptions,
} from '@/components/People/transformOptions';
import {
  getOverlappingLeaves,
  isEmployeeOnLeaveDuring,
} from '@/helpers/isEmployeeOnLeave';
import { useLazyGetOrganizationEmployeesQuery } from '@/redux/slices/organizationApiSlice';
import { useIsAdpWfnCanadaConnectedOrg } from '@/hooks/useIsAdpWfnCanadaConnectedOrg';
import { EmployeeLeave } from '@/types/employees';

type EmployeeManagerSelectorProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  label: string;
  organizationId: string;
  name?: string;
  id?: string;
  excludeEmployeeId?: string;
  placeholder?: string;
  disabled?: boolean;
  helperText?: string;
  // Passing these opts the selector into disabling/annotating candidates
  // already known to be on leave during the window below (e.g. the leave
  // forwardee picker) - keyed by employee id, same shape as the reassignment
  // pickers' bulk leave lookup. Omitted entirely by callers that don't need
  // this (e.g. picking an employee's actual manager), which behave exactly
  // as before.
  employeeLeavesById?: Record<string, EmployeeLeave[]>;
  isLoadingEmployeeLeaves?: boolean;
  isErrorEmployeeLeaves?: boolean;
  leaveStartDate?: Date | null;
  leaveReturnDate?: Date | null;
};

const EmployeeManagerSelector = ({
  control,
  label,
  organizationId,
  name = 'manager',
  id = 'employee-manager-search-selector',
  excludeEmployeeId,
  placeholder,
  disabled,
  helperText,
  employeeLeavesById,
  isLoadingEmployeeLeaves,
  isErrorEmployeeLeaves,
  leaveStartDate = null,
  leaveReturnDate = null,
}: EmployeeManagerSelectorProps) => {
  const [searchForEmployees] = useLazyGetOrganizationEmployeesQuery();
  const { t, i18n } = useTranslation('common');
  const isAdpWfnCanada = useIsAdpWfnCanadaConnectedOrg();

  const isOptionDisabled = (option: EmployeeOption) =>
    leaveStartDate !== null &&
    isEmployeeOnLeaveDuring(
      employeeLeavesById?.[option.value],
      leaveStartDate,
      leaveReturnDate
    );

  return (
    <SearchAsyncSelectField<EmployeeOption>
      key={i18n.language}
      id={id}
      name={name}
      control={control}
      label={label}
      placeholder={placeholder}
      helperText={helperText}
      defaultOptions
      cacheOptions
      isSearchable
      isClearable
      // Until the leave check itself resolves, employeeLeavesById is
      // undefined for every option (isOptionDisabled below defaults to
      // false), which would let an on-leave employee be picked in that
      // window - and if the fetch fails outright, it never resolves at all.
      // Disable the whole select in both cases - same as the reassignment
      // pickers (LeaveReassignRow).
      disabled={disabled || isLoadingEmployeeLeaves || isErrorEmployeeLeaves}
      isOptionDisabled={leaveStartDate !== null ? isOptionDisabled : undefined}
      customComponents={{
        // isDisabled here is the fully-resolved state react-select computed
        // from isOptionDisabled above (arc's own Option wrapper only dims
        // plain text automatically, and EmployeeSelectOption renders its own
        // explicit Text elements, so this must be passed through explicitly).
        Option: option =>
          leaveStartDate !== null &&
          option.data.value !== EMPLOYEE_SEARCH_HINT_VALUE ? (
            <EmployeeSelectOption
              data={option.data}
              isDisabled={option.isDisabled}
              nameAdornment={
                <EmployeeOnLeaveIcon
                  employeeId={option.data.value}
                  // Filtered to the leaves that actually overlap this
                  // window, not just any in-progress/scheduled leave the
                  // employee has, so the icon never disagrees with
                  // isOptionDisabled.
                  leaves={getOverlappingLeaves(
                    employeeLeavesById?.[option.data.value],
                    leaveStartDate,
                    leaveReturnDate
                  )}
                  leavesLoading={isLoadingEmployeeLeaves}
                />
              }
            />
          ) : (
            <EmployeeSelectOption data={option.data} />
          ),
      }}
      fetchOptions={async (term: string) => {
        try {
          const employees = await searchForEmployees({
            organizationId,
            ...(term && { term }),
            offset: 0,
            limit: 20,
            orderBy: 'last_name',
            sortOrder: 'asc',
          }).unwrap();

          const filtered = excludeEmployeeId
            ? employees.filter(e => e.id !== excludeEmployeeId)
            : employees;

          const options = transformEmployeesToOptions(
            filtered,
            i18n.language,
            isAdpWfnCanada
          );
          return !term
            ? appendSearchHint(options, t('employeeSearchHint'))
            : options;
        } catch {
          return [];
        }
      }}
    />
  );
};

export default EmployeeManagerSelector;
